import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { Order } from '@components/table/types';
import { API_RESPONSE_STATUS } from '@constants/common';
import { ToastTypes } from '@type/toast';
import { showGenericErrorToast } from '@utils/error';
import displayToast from '@components/toast/customToast';

import {
  useDeleteCompanyInvestorMutation,
  useLazyGetInvestorQuery,
  useUpdateInvestorMutation
} from '../api';
import { InvestorsTableLimit } from '../constants';
import { ExternalInvestorType } from '../types';
import { formatInvestors } from '../services/investors';

export type InvestorsTableHeader =
  | 'name'
  | 'url'
  | 'designation'
  | 'visibleInDeal';

export type InvestorsTableData = {
  id: string;
  name: string;
  designation: string;
  description: string;
  url: string;
  logoPath: string;
  userId: string;
  type: ExternalInvestorType;
  roundId: string;
  visibleInDeal: boolean;
  createdAt: string;
  order: number;
};

const useGetInvestors = (roundId?: string) => {
  const [sortInstitutionalOrder, setSortInstitutionalOrder] =
    useState<Order>('asc');
  const [sortInstitutionalBy, setSortInstitutionalBy] =
    useState<InvestorsTableHeader>('name');
  const [institutionalLimit, setInstitutionalLimit] =
    useState(InvestorsTableLimit);
  const [institutionalOffset, setInstitutionalOffset] = useState(0);
  const [institutionalSearchTerm, setInstitutionalSearchTerm] = useState('');

  const [sortAngelOrder, setSortAngelOrder] = useState<Order>('asc');
  const [sortAngelBy, setSortAngelBy] = useState<InvestorsTableHeader>('name');
  const [angelLimit, setAngelLimit] = useState(InvestorsTableLimit);
  const [angelOffset, setAngelOffset] = useState(0);
  const [angelSearchTerm, setAngelSearchTerm] = useState('');

  const { companyId } = useParams();

  const [institutionalInvestorsData, setInstitutionalInvestorsData] = useState<
    InvestorsTableData[]
  >([]);
  const [angelInvestorsData, setAngelInvestorsData] = useState<
    InvestorsTableData[]
  >([]);

  const [debouncedInstitutionalSearchText] = useDebounce(
    institutionalSearchTerm,
    300
  );
  const [debouncedAngelSearchText] = useDebounce(angelSearchTerm, 300);

  const [
    getInstitutionalInvestors,
    { data: institutionalInvestorsList, isLoading: isInstitutionalLoading }
  ] = useLazyGetInvestorQuery();
  const [
    getAngelInvestors,
    { data: angelInvestorsList, isLoading: isAngelLoading }
  ] = useLazyGetInvestorQuery();

  const [
    deleteInvestor,
    { data: deleteInvestorResponse, isLoading: deleteInvestorLoading }
  ] = useDeleteCompanyInvestorMutation();

  const [updateInvestor] = useUpdateInvestorMutation();

  const getInstitutionalMembers = async () => {
    try {
      const { data } = await getInstitutionalInvestors({
        companyId: companyId ?? '',
        payload: {
          limit: institutionalLimit,
          offset: institutionalOffset,
          // order: sortInstitutionalOrder,
          search: debouncedInstitutionalSearchText,
          // sort: sortByKeyMap[sortInstitutionalBy]
          //   ? sortByKeyMap[sortInstitutionalBy]
          //   : 'name',
          roundId: roundId,
          type: ExternalInvestorType.InstitutionalInvestor
        }
      }).unwrap();

      if (institutionalOffset === 0) {
        const newInvestors = formatInvestors(data);
        setInstitutionalInvestorsData(newInvestors);
      } else {
        const newInvestors = formatInvestors(data);
        setInstitutionalInvestorsData([
          ...institutionalInvestorsData,
          ...newInvestors
        ]);
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  const getAngelMembers = async () => {
    try {
      const { data } = await getAngelInvestors({
        companyId: companyId ?? '',
        payload: {
          limit: angelLimit,
          offset: angelOffset,
          // order: sortAngelOrder,
          search: debouncedAngelSearchText,
          // sort: sortByKeyMap[sortAngelBy] ? sortByKeyMap[sortAngelBy] : 'name',
          roundId: roundId,
          type: ExternalInvestorType.SuperAngelInvestor
        }
      }).unwrap();

      if (angelOffset === 0) {
        const newInvestors = formatInvestors(data);
        setAngelInvestorsData(newInvestors);
      } else {
        const newInvestors = formatInvestors(data);
        setAngelInvestorsData([...angelInvestorsData, ...newInvestors]);
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  const handleDeleteInvestor = async (investorId: string) => {
    if (investorId) {
      try {
        const response = await deleteInvestor({
          companyId: companyId ?? '',
          investorId: investorId
        }).unwrap();
        if (response && response.status === API_RESPONSE_STATUS.OK) {
          displayToast(ToastTypes.SUCCESS, 'Investor Deleted Successfully');
          setInstitutionalOffset(0);
          setAngelOffset(0);
        } else
          displayToast(
            ToastTypes.ERROR,
            response.error?.details?.[0] ?? 'Failed to Delete Investor'
          );
      } catch (err) {
        showGenericErrorToast(err);
      }
    }
  };

  const handleVisibleInDealChange = async (
    val: boolean,
    data: InvestorsTableData,
    investorId: string
  ) => {
    try {
      const response = await updateInvestor({
        companyId: companyId ?? '',
        payload: {
          name: data.name,
          designation: data.designation,
          description: data.description,
          url: data.url,
          logo_path: data.logoPath,
          user_id: data.userId,
          type: data.type,
          round_id: data.roundId,
          is_visible_in_deal: val,
          created_at: data.createdAt,
          order: data.order
        },
        investorId: investorId
      }).unwrap();
      if (response.status === 'ok') {
        displayToast(
          ToastTypes.SUCCESS,
          'Successfully updated visible in deal'
        );
      } else {
        displayToast(
          ToastTypes.ERROR,
          response.error?.details?.[0] ?? 'Failed to update visible in deal'
        );
      }
    } catch (err) {
      showGenericErrorToast(err);
    }
  };

  useEffect(() => {
    getInstitutionalMembers();
  }, [
    sortInstitutionalBy,
    sortInstitutionalOrder,
    institutionalLimit,
    institutionalOffset,
    institutionalInvestorsList,
    roundId
  ]);

  useEffect(() => {
    getAngelMembers();
  }, [
    sortAngelBy,
    sortAngelOrder,
    angelLimit,
    angelOffset,
    angelInvestorsList,
    roundId
  ]);

  useEffect(() => {
    if (institutionalOffset) {
      setInstitutionalOffset(0);
    } else {
      getInstitutionalMembers();
    }
  }, [debouncedInstitutionalSearchText]);

  useEffect(() => {
    if (angelOffset) {
      setAngelOffset(0);
    } else {
      getAngelMembers();
    }
  }, [debouncedAngelSearchText]);

  return {
    sortInstitutionalOrder,
    setSortInstitutionalOrder,
    sortInstitutionalBy,
    setSortInstitutionalBy,
    institutionalLimit,
    institutionalOffset,
    setInstitutionalLimit,
    setInstitutionalOffset,
    setInstitutionalSearchTerm,
    institutionalSearchTerm,
    institutionalInvestorsData,
    totalInstitutionalRows: institutionalInvestorsList?.total_count,
    maxInstitutionalOrder: institutionalInvestorsList?.max_order,
    isInstitutionalLoading,
    deleteInvestorLoading,
    sortAngelOrder,
    setSortAngelOrder,
    sortAngelBy,
    setSortAngelBy,
    angelLimit,
    angelOffset,
    setAngelLimit,
    setAngelOffset,
    setAngelSearchTerm,
    angelSearchTerm,
    angelInvestorsData,
    totalAngelRows: angelInvestorsList?.total_count,
    maxAngelOrder: institutionalInvestorsList?.max_order,
    isAngelLoading,
    handleDeleteInvestor,
    deleteInvestorResponse,
    handleVisibleInDealChange
  };
};

export default useGetInvestors;
